import { Markdown } from '@copilotkit/react-ui';
import { spaces, ColorsV2, getToken, sizes } from '@electricjs/arc';
import styled from 'styled-components';
import type { CopilotKitMessage } from './types';
import { getMessageContent } from './types';

const UserMessageBubble = styled.div`
  width: fit-content;
  padding: ${spaces.space_xs_rem} ${spaces.space_m_rem};
  margin-bottom: ${spaces.space_m_rem};
  margin-right: ${spaces.space_xs_rem}; /* Small margin for scrollbar spacing */
  background-color: ${getToken(ColorsV2.PRIMARY_LIGHT)};
  border-radius: ${sizes.size_8_rem} ${sizes.size_8_rem} 0 ${sizes.size_8_rem};
  word-break: break-word;
  overflow-wrap: break-word;
  align-self: flex-end;

  /* Apply Inter font family to match Arc design system */
  font-family:
    'Inter, sans-serif',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Helvetica Neue',
    Arial,
    sans-serif;
  font-weight: 400;
  line-height: 1.5;

  /* Theme-aware text color: TEXT (light) in dark mode, TEXT_INVERSE (light) in light mode for visibility */
  color: ${props =>
    props.theme.name === 'Electric Dark'
      ? getToken(ColorsV2.TEXT)
      : getToken(ColorsV2.TEXT_INVERSE)};

  /* Override markdown content font size and color for user messages */
  .copilotKitMarkdownElement,
  .copilotKitMarkdownElement * {
    font-size: ${sizes.size_16_rem};
    color: ${props =>
      props.theme.name === 'Electric Dark'
        ? getToken(ColorsV2.TEXT)
        : getToken(ColorsV2.TEXT_INVERSE)};
  }

  /* Table cells aren't given the copilotKitMarkdownElement class by CopilotKit,
     so they need the theme color applied separately to stay readable in dark mode */
  .copilotKitMarkdown table,
  .copilotKitMarkdown table * {
    color: ${props =>
      props.theme.name === 'Electric Dark'
        ? getToken(ColorsV2.TEXT)
        : getToken(ColorsV2.TEXT_INVERSE)};
  }
`;

type UserMessageProps = {
  message: CopilotKitMessage;
};

/**
 * UserMessage - Displays messages from the user
 */
export const UserMessage = ({ message }: UserMessageProps) => {
  // Extract content using type-safe helper function
  const content = getMessageContent(message);

  // Don't render if no content
  if (!content.trim()) {
    return null;
  }

  return (
    <UserMessageBubble>
      <Markdown content={content} />
    </UserMessageBubble>
  );
};
